import React, { useCallback, useMemo } from "react";
import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { MenuItem, Option } from "@shared/interfaces";
import { Logo } from "@shared/components/Logo";
import { AsyncSelectPickerInput, Button } from "@shared/components/Common";
import { Icon } from "@shared/components/Icon";
import { actions as hospitalActions, selectors as hospitalSelectors } from "@containers/Hospital/store";
import { Hospital } from "@shared/models";

import { SidebarItems } from "../SidebarItems";

import "./index.scss";

export interface MainSidebarProps {
  items: MenuItem[];
  footerItems: MenuItem[];
  onLogoutClick: () => void;
}

const Sidebar = (props: MainSidebarProps) => {
  const { items, footerItems, onLogoutClick } = props;

  const dispatch = useDispatch();
  const hospitals = useSelector(hospitalSelectors.getHospitals());
  const hospital = useSelector(hospitalSelectors.getHospital());

  const hasMultipleHospitals = useMemo(() => {
    const MIN_HOSPITAL_COUNT = 1;
    return hospitals.length && hospitals.length > MIN_HOSPITAL_COUNT;
  }, [hospitals.length]);

  const getHospitalData = useCallback((search: string) => {
    return hospitalActions.getHospitals.request({
      search,
      page: 0,
      limit: 20,
    });
  }, []);

  const prepareOptionFunction = useCallback((hospital: Hospital) => {
    return { value: String(hospital.id), label: hospital.name };
  }, []);

  const handleChangeHospital = useCallback(
    (option: Option | null) => {
      if (option) {
        dispatch(hospitalActions.getHospital.request({ id: Number(option.value) }));
      }
    },
    [dispatch],
  );

  return (
    <div className="main-sidebar">
      <div className="sidebar-logo">
        <Logo />
      </div>

      <div className={classnames("sidebar-hospital", { hidden: !hasMultipleHospitals })}>
        <AsyncSelectPickerInput
          disableInitRequest={true}
          name="hospital"
          className="select-picker"
          onChange={handleChangeHospital}
          value={hospital ? { value: String(hospital.id), label: hospital.name } : null}
          placeholder="Select Hospital"
          getData={getHospitalData}
          selectData={hospitalSelectors.getHospitals}
          loadingAction={hospitalActions.getHospitals}
          prepareOptionFunction={prepareOptionFunction}
          selectFirst={true}
          notCleanable={true}
        />
      </div>

      <div className="sidebar-items">
        <SidebarItems items={items} />
      </div>
      <div className="sidebar-footer">
        <SidebarItems items={footerItems} />
        <div className="sidebar-logout">
          <Button onClick={onLogoutClick} className="clear sidebar-footer-btn">
            <Icon type="logout" />
            Log Out
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
